import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, UnderlineType, Color } from 'docx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import html2pdf from 'html2pdf.js';
import html2canvas from 'html2canvas';

class FileService {
  constructor() {
    this.recentFiles = this.loadRecentFiles();
  }

  // PDF로 저장
  async saveAsPDF(content, filename = null) {
    try {
      const fileName = filename || `문서_${new Date().toISOString().slice(0, 10)}.pdf`;

      // 미리보기와 동일한 DOM을 캡처하여 PDF로 저장
      let exportNode = document.getElementById('export-preview-area');
      
      // export-preview-area를 찾을 수 없는 경우 임시 요소 생성
      if (!exportNode) {
        console.warn('export-preview-area를 찾을 수 없어 임시 요소를 생성합니다.');
        
        // 임시 DOM 요소 생성
        const tempDiv = document.createElement('div');
        tempDiv.style.position = 'absolute';
        tempDiv.style.left = '-9999px';
        tempDiv.style.width = '210mm';
        tempDiv.style.padding = '20px';
        tempDiv.style.fontFamily = 'Segoe UI, sans-serif';
        tempDiv.style.fontSize = '14px';
        tempDiv.style.lineHeight = '1.6';
        tempDiv.style.color = '#24292e';
        tempDiv.style.backgroundColor = '#ffffff';
        
        // 마크다운을 HTML로 변환
        const htmlContent = this.convertMarkdownToHTML(content);
        tempDiv.innerHTML = htmlContent;
        
        document.body.appendChild(tempDiv);
        exportNode = tempDiv;
      }

      const opt = {
        margin:       [20, 20, 20, 20],
        filename:     fileName,
        image:        { type: 'jpeg', quality: 0.98 },
        html2canvas:  { scale: 2, useCORS: true, backgroundColor: '#ffffff', scrollY: 0 },
        pagebreak:    { mode: ['css', 'legacy'] },
        jsPDF:        { unit: 'pt', format: 'a4', orientation: 'portrait' }
      };

      // 문서 길이 측정 후 긴 문서는 바로 슬라이싱 경로 사용
      const totalHeight = exportNode.scrollHeight || exportNode.offsetHeight || 0;
      const isVeryLong = totalHeight > 12000 || (content && content.length > 100000);

      // 시작 이벤트
      try { window.dispatchEvent(new CustomEvent('pdfStart', { detail: {} })); } catch (_) {}

      if (isVeryLong) {
        await this.renderLargePDFBySlicing(exportNode, fileName);
      } else {
        // 1차: 기본 html2pdf 경로 (가벼운 문서용)
        try {
          await html2pdf().set(opt).from(exportNode).save();
        } catch (heavyError) {
          console.warn('html2pdf 기본 경로 실패, 이미지 슬라이싱 경로로 폴백:', heavyError);
          // 2차: 페이지 단위 이미지 슬라이싱으로 PDF 생성 (긴 문서/80+ 페이지 대응)
          await this.renderLargePDFBySlicing(exportNode, fileName);
        }
      }
      
      // 임시 요소였다면 제거
      if (!document.getElementById('export-preview-area') && exportNode.parentNode) {
        document.body.removeChild(exportNode);
      }

      // 완료 이벤트
      try { window.dispatchEvent(new CustomEvent('pdfDone', { detail: {} })); } catch (_) {}

      // 최근 파일 목록에 추가
      this.addToRecentFiles({
        name: fileName,
        content: content,
        savedAt: new Date().toISOString(),
        type: 'pdf'
      });

      return { success: true, filename: fileName };
    } catch (error) {
      console.error('PDF 파일 저장 실패:', error);
      try { window.dispatchEvent(new CustomEvent('pdfDone', { detail: { error: error?.message } })); } catch (_) {}
      return { success: false, error: error.message };
    }
  }

  // 긴 문서용: 요소를 페이지 높이로 잘라 이미지로 붙여 PDF 생성
  async renderLargePDFBySlicing(sourceNode, fileName) {
    // html2canvas 모듈 사용 (직접 임포트)
    // A4 크기 (pt): 595 x 842. html2canvas는 px 기준이라 변환 필요.
    // 96 DPI 기준 A4 px ≈ 794 x 1123, 여백 감안해서 내부 렌더 폭을 794px로 맞추고 scale=2로 선명도 확보
    const targetWidthPx = 794; // 약 A4 너비(px @96dpi)
    // 페이지 수가 많을수록 캡처 배율을 낮춰 메모리/속도를 확보
    // 0~60p: 2.0, 60~120p: 1.5, 120p+: 1.2
    const estimateLines = (sourceNode.textContent || '').length / 80; // 대략 라인 추정
    const estimatePages = Math.max(1, Math.ceil(estimateLines / 40));
    const dynamicScale = estimatePages > 120 ? 1.2 : estimatePages > 60 ? 1.5 : 2;
    const scale = dynamicScale;
    const pageWidthPx = targetWidthPx;
    const pageHeightPx = Math.floor(1123); // 약 A4 높이(px @96dpi)

    // 렌더링 전용 복제 노드 (스타일 유지)
    const clone = sourceNode.cloneNode(true);
    const wrapper = document.createElement('div');
    wrapper.style.position = 'absolute';
    wrapper.style.left = '-99999px';
    wrapper.style.top = '0';
    wrapper.style.width = pageWidthPx + 'px';
    wrapper.style.background = '#ffffff';
    wrapper.style.boxSizing = 'border-box';
    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);

    // 실제 컨텐츠 전체 높이 계산
    const totalHeight = clone.scrollHeight || clone.offsetHeight || wrapper.scrollHeight;
    const totalPages = Math.max(1, Math.ceil(totalHeight / pageHeightPx));

    const pdf = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'portrait' });
    const pdfWidthPt = pdf.internal.pageSize.getWidth();
    const pdfHeightPt = pdf.internal.pageSize.getHeight();

    // 시작 시 총 페이지 정보 브로드캐스트
    try { window.dispatchEvent(new CustomEvent('pdfProgress', { detail: { page: 0, totalPages, percent: 0 } })); } catch (_) {}

    for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
      const offsetY = pageIndex * pageHeightPx;
      // 페이지 클리핑 박스
      const pageContainer = document.createElement('div');
      pageContainer.style.position = 'relative';
      pageContainer.style.width = pageWidthPx + 'px';
      pageContainer.style.height = pageHeightPx + 'px';
      pageContainer.style.overflow = 'hidden';
      pageContainer.style.background = '#ffffff';

      // 내부 컨텐츠를 위로 올려서 해당 페이지 영역만 보이도록 설정
      const inner = clone.cloneNode(true);
      inner.style.margin = '0';
      inner.style.transform = `translateY(-${offsetY}px)`;
      inner.style.willChange = 'transform';
      pageContainer.appendChild(inner);
      wrapper.appendChild(pageContainer);

      // html2canvas로 현재 페이지 캡처
      const canvas = await html2canvas(pageContainer, {
        scale,
        useCORS: true,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.92);

      if (pageIndex > 0) pdf.addPage('a4', 'portrait');
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidthPt, pdfHeightPt, undefined, 'FAST');

      // 메모리 해제
      wrapper.removeChild(pageContainer);

      // 진행 이벤트 브로드캐스트 (옵셔널 UI용)
      try {
        const percent = Math.round(((pageIndex + 1) / totalPages) * 100);
        window.dispatchEvent(new CustomEvent('pdfProgress', { detail: { page: pageIndex + 1, totalPages, percent } }));
      } catch (_) {}

      // UI 응답성 확보: 이벤트 루프에 양보
      await new Promise(resolve => setTimeout(resolve, 0));
    }

    // 결과 저장
    pdf.save(fileName);
    // 정리
    if (wrapper && wrapper.parentNode) {
      wrapper.parentNode.removeChild(wrapper);
    }
  }

  // 마크다운을 HTML로 변환
  convertMarkdownToHTML(content) {
    let html = '';
    const lines = content.split('\n');
    
    for (const line of lines) {
      const trimmed = line.trim();
      
      if (trimmed === '') {
        html += '<br>';
      } else if (trimmed.startsWith('# ')) {
        html += `<h1 style="font-size: 24px; font-weight: bold; margin: 20px 0 10px 0;">${trimmed.substring(2)}</h1>`;
      } else if (trimmed.startsWith('## ')) {
        html += `<h2 style="font-size: 20px; font-weight: bold; margin: 18px 0 8px 0;">${trimmed.substring(3)}</h2>`;
      } else if (trimmed.startsWith('### ')) {
        html += `<h3 style="font-size: 18px; font-weight: bold; margin: 16px 0 6px 0;">${trimmed.substring(4)}</h3>`;
      } else if (trimmed.startsWith('#### ')) {
        html += `<h4 style="font-size: 16px; font-weight: bold; margin: 14px 0 4px 0;">${trimmed.substring(5)}</h4>`;
      } else if (trimmed.startsWith('##### ')) {
        html += `<h5 style="font-size: 14px; font-weight: bold; margin: 12px 0 2px 0;">${trimmed.substring(6)}</h5>`;
      } else if (trimmed.startsWith('###### ')) {
        html += `<h6 style="font-size: 12px; font-weight: bold; margin: 10px 0;">${trimmed.substring(7)}</h6>`;
      } else {
        // 볼드와 이탤릭 처리
        let processedLine = trimmed;
        processedLine = processedLine.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        processedLine = processedLine.replace(/\*(.*?)\*/g, '<em>$1</em>');
        html += `<p style="margin: 10px 0;">${processedLine}</p>`;
      }
    }
    
    return html;
  }

  // 워드 파일로 저장
  async saveAsWord(content, filename = null) {
    try {
      // 마크다운을 워드 문서로 변환
      const doc = this.convertMarkdownToWord(content);
      
      // 파일명 생성
      const fileName = filename || `문서_${new Date().toISOString().slice(0, 10)}.docx`;
      
      // 워드 파일 생성 (브라우저 환경에서는 blob 방식 사용)
      const blob = await Packer.toBlob(doc);
      
      // 파일 저장
      saveAs(blob, fileName);
      
      // 최근 파일 목록에 추가
      this.addToRecentFiles({
        name: fileName,
        content: content,
        savedAt: new Date().toISOString(),
        type: 'word'
      });
      
      return { success: true, filename: fileName };
    } catch (error) {
      console.error('워드 파일 저장 실패:', error);
      return { success: false, error: error.message };
    }
  }

  // 마크다운을 워드 문서로 변환
  convertMarkdownToWord(content) {
    const paragraphs = [];
    const lines = content.split('\n');
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      if (line === '') {
        paragraphs.push(new Paragraph({ 
          children: [new TextRun({ text: '' })],
          spacing: {
            after: 160, // 8pt after
          }
        }));
        continue;
      }
      
      // 제목 처리
      if (line.startsWith('# ')) {
        paragraphs.push(new Paragraph({
          children: [new TextRun({
            text: line.substring(2),
            bold: true,
            size: 48, // 24pt
            color: "24292E",
            font: "Segoe UI",
          })],
          spacing: {
            before: 480, // 24pt before
            after: 240,  // 12pt after
            line: 360,   // 1.5 line spacing
          },
        }));
      } else if (line.startsWith('## ')) {
        paragraphs.push(new Paragraph({
          children: [new TextRun({
            text: line.substring(3),
            bold: true,
            size: 40, // 20pt
            color: "24292E",
            font: "Segoe UI",
          })],
          spacing: {
            before: 400, // 20pt before
            after: 200,  // 10pt after
            line: 360,   // 1.5 line spacing
          },
        }));
      } else if (line.startsWith('### ')) {
        paragraphs.push(new Paragraph({
          children: [new TextRun({
            text: line.substring(4),
            bold: true,
            size: 36, // 18pt
            color: "24292E",
            font: "Segoe UI",
          })],
          spacing: {
            before: 320, // 16pt before
            after: 160,  // 8pt after
            line: 360,   // 1.5 line spacing
          },
        }));
      } else if (line.startsWith('#### ')) {
        paragraphs.push(new Paragraph({
          children: [new TextRun({
            text: line.substring(5),
            bold: true,
            size: 32, // 16pt
            color: "24292E",
            font: "Segoe UI",
          })],
          spacing: {
            before: 280, // 14pt before
            after: 120,  // 6pt after
            line: 360,   // 1.5 line spacing
          },
        }));
      } else if (line.startsWith('##### ')) {
        paragraphs.push(new Paragraph({
          children: [new TextRun({
            text: line.substring(6),
            bold: true,
            size: 28, // 14pt
            color: "24292E",
            font: "Segoe UI",
          })],
          spacing: {
            before: 240, // 12pt before
            after: 80,   // 4pt after
            line: 360,   // 1.5 line spacing
          },
        }));
      } else if (line.startsWith('###### ')) {
        paragraphs.push(new Paragraph({
          children: [new TextRun({
            text: line.substring(7),
            bold: true,
            size: 24, // 12pt
            color: "24292E",
            font: "Segoe UI",
          })],
          spacing: {
            before: 200, // 10pt before
            after: 40,   // 2pt after
            line: 360,   // 1.5 line spacing
          },
        }));
      } else {
        // 일반 텍스트 처리 (볼드, 이탤릭 등)
        const processedText = this.processInlineFormatting(line);
        paragraphs.push(new Paragraph({
          children: processedText,
          spacing: {
            line: 360, // 1.5 line spacing
            after: 160, // 8pt after
          },
        }));
      }
    }
    
    return new Document({
      styles: {
        default: {
          document: {
            run: {
              font: "Segoe UI",
              size: 24, // 12pt
              color: "24292E", // 기본 텍스트 색상
            },
            paragraph: {
              spacing: {
                line: 360, // 1.5 line spacing
                before: 0,
                after: 200, // 10pt after
              },
            },
          },
        },
      },
      sections: [{
        properties: {
          page: {
            margin: {
              top: 1440, // 1 inch
              right: 1440,
              bottom: 1440,
              left: 1440,
            },
          },
        },
        children: paragraphs,
      }],
    });
  }

  // 인라인 포맷팅 처리 (볼드, 이탤릭 등)
  processInlineFormatting(text) {
    const children = [];
    let currentText = text;
    
    // 볼드 처리 (**text**)
    const boldRegex = /\*\*(.*?)\*\*/g;
    let lastIndex = 0;
    let match;
    
    while ((match = boldRegex.exec(currentText)) !== null) {
      // 볼드 앞의 텍스트
      if (match.index > lastIndex) {
        children.push(new TextRun({ 
          text: currentText.slice(lastIndex, match.index),
          font: "Segoe UI",
          size: 24,
          color: "24292E"
        }));
      }
      
      // 볼드 텍스트
      children.push(new TextRun({ 
        text: match[1], 
        bold: true,
        font: "Segoe UI",
        size: 24,
        color: "24292E"
      }));
      
      lastIndex = match.index + match[0].length;
    }
    
    // 남은 텍스트
    if (lastIndex < currentText.length) {
      children.push(new TextRun({ 
        text: currentText.slice(lastIndex),
        font: "Segoe UI",
        size: 24,
        color: "24292E"
      }));
    }
    
    return children.length > 0 ? children : [new TextRun({ 
      text: currentText,
      font: "Segoe UI",
      size: 24,
      color: "24292E"
    })];
  }

  // 최근 파일 목록에 추가
  addToRecentFiles(fileInfo) {
    // 중복 제거
    this.recentFiles = this.recentFiles.filter(file => file.name !== fileInfo.name);
    
    // 새 파일을 맨 앞에 추가
    this.recentFiles.unshift(fileInfo);
    
    // 파일은 content를 포함하므로 대화보다 적게 유지 (최대 30개)
    // 용량 문제를 고려하여 대화(50개)보다 적은 수로 제한
    if (this.recentFiles.length > 30) {
      this.recentFiles = this.recentFiles.slice(0, 30);
    }
    
    this.saveRecentFiles();
  }

  // 최근 파일 목록 가져오기
  getRecentFiles() {
    return this.recentFiles;
  }

  // 최근 파일 추가 (addToRecentFiles의 별칭)
  addRecentFile(fileInfo) {
    this.addToRecentFiles(fileInfo);
  }

  // 최근 파일 불러오기
  loadRecentFile(fileName) {
    const file = this.recentFiles.find(f => f.name === fileName);
    if (!file) return null;
    // content가 없는 경우 (용량 절감으로 제거된 경우) null 반환
    if (file.hasNoContent || !file.content) {
      console.warn(`파일 "${fileName}"의 내용이 용량 절감으로 인해 제거되었습니다.`);
      return null;
    }
    return file.content;
  }

  // 최근 파일 목록 저장
  saveRecentFiles() {
    try {
      localStorage.setItem('ai_agent_recent_files', JSON.stringify(this.recentFiles));
    } catch (error) {
      console.warn('최근 파일 목록 저장 실패 (용량 초과 가능), 파일 수 줄이는 중...', error);
      // 저장 실패 시 파일 수를 줄여서 재시도 (용량 문제 해결)
      try {
        // 30개 → 20개로 줄이기
        if (this.recentFiles.length > 20) {
          this.recentFiles = this.recentFiles.slice(0, 20);
          localStorage.setItem('ai_agent_recent_files', JSON.stringify(this.recentFiles));
          return;
        }
        // 20개도 실패하면 10개로
        if (this.recentFiles.length > 10) {
          this.recentFiles = this.recentFiles.slice(0, 10);
          localStorage.setItem('ai_agent_recent_files', JSON.stringify(this.recentFiles));
          return;
        }
        // 10개도 실패하면 오래된 파일부터 content 제거 (메타데이터만 유지)
        if (this.recentFiles.length > 5) {
          // 오래된 파일부터 content를 제거하여 용량 절감
          const sortedByDate = [...this.recentFiles].sort((a, b) => 
            new Date(a.savedAt || 0) - new Date(b.savedAt || 0)
          );
          // 오래된 파일 5개의 content만 제거 (최신 5개는 유지)
          for (let i = 0; i < Math.min(5, sortedByDate.length - 5); i++) {
            const oldFile = this.recentFiles.find(f => f.name === sortedByDate[i].name);
            if (oldFile && oldFile.content) {
              delete oldFile.content; // content 제거하여 용량 절감
              oldFile.hasNoContent = true; // content가 없음을 표시
            }
          }
          localStorage.setItem('ai_agent_recent_files', JSON.stringify(this.recentFiles));
          return;
        }
      } catch (retryError) {
        console.error('최근 파일 목록 재저장도 실패:', retryError);
        // 최후의 수단: 오래된 파일부터 제거
        try {
          this.recentFiles = this.recentFiles.slice(0, 5);
          localStorage.setItem('ai_agent_recent_files', JSON.stringify(this.recentFiles));
        } catch (finalError) {
          console.error('최근 파일 목록 저장 완전 실패:', finalError);
        }
      }
    }
  }

  // 최근 파일 목록 불러오기
  loadRecentFiles() {
    try {
      const saved = localStorage.getItem('ai_agent_recent_files');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('최근 파일 목록 불러오기 실패:', error);
      return [];
    }
  }

  // 파일 삭제
  deleteRecentFile(fileName) {
    this.recentFiles = this.recentFiles.filter(file => file.name !== fileName);
    this.saveRecentFiles();
  }

  // 파일 이름 변경
  renameRecentFile(oldName, newName) {
    const file = this.recentFiles.find(f => f.name === oldName);
    if (file) {
      file.name = newName;
      // path도 업데이트 (있는 경우)
      if (file.path === oldName) {
        file.path = newName;
      }
      this.saveRecentFiles();
      return true;
    }
    return false;
  }

  // 텍스트 파일로 저장 (백업용)
  saveAsText(content, filename = null) {
    try {
      const fileName = filename || `문서_${new Date().toISOString().slice(0, 10)}.txt`;
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      saveAs(blob, fileName);
      
      this.addToRecentFiles({
        name: fileName,
        content: content,
        savedAt: new Date().toISOString(),
        type: 'text'
      });
      
      return { success: true, filename: fileName };
    } catch (error) {
      console.error('텍스트 파일 저장 실패:', error);
      return { success: false, error: error.message };
    }
  }
}

// 싱글톤 인스턴스 생성
const fileService = new FileService();
export default fileService;
